import { enablePush } from '../lib/push';
import { useState } from 'react';
import { api } from '../lib/api';
import { ago } from '../lib/format';
import { useQuery } from '../lib/query';
import { Link, back } from '../lib/router';
import { setState, toast, useApp } from '../lib/store';
import { Icon, type IconName } from '../components/Icon';
import { AppShell, Loading, PageHeader } from '../components/Shell';
import type { AppNotification } from '../lib/types';

const KIND: Record<AppNotification['kind'], { icon: IconName; tab: string }> = {
  episode: { icon: 'play', tab: 'New episodes' },
  payment: { icon: 'rupee', tab: 'Payments' },
  reminder: { icon: 'reels', tab: 'All' },
  recommendation: { icon: 'spark', tab: 'New episodes' },
};

export function Notifications() {
  const extra = useApp((s) => s.extraNotifs.length);
  const read = useApp((s) => s.readNotifs);
  const push = useApp((s) => s.settings.push);
  const { data, loading } = useQuery('notifs:' + extra, () => api.notifications());
  const [tab, setTab] = useState('All');
  const list = (data || []).filter((n) => tab === 'All' || KIND[n.kind].tab === tab);

  const markAll = () => setState({ readNotifs: Object.fromEntries((data || []).map((n) => [n.id, true as const])) });
  const markOne = (id: string) => setState((s) => ({ readNotifs: { ...s.readNotifs, [id]: true } }));
  const enable = async () => {
    try {
      const r = await enablePush();
      if (r === 'granted') { setState((s) => ({ settings: { ...s.settings, push: true } })); toast('Notifications are on'); }
      else toast(r === 'unsupported' ? 'Notifications are not available here.' : 'Notifications are blocked. Turn them on in your phone or browser settings.');
    } catch {
      toast('Notifications are not available here.');
    }
  };

  return (
    <AppShell mobileHeader={false}>
      <div className="page narrow">
        <PageHeader title="Notifications" onBack={() => back('/home')} action={<button type="button" className="text-link" onClick={markAll}>Mark all read</button>} />
        {!push && (
          <div className="push-card">
            <span className="push-icon"><Icon name="bell" /></span>
            <div>
              <b>Never miss a new episode</b>
              <p className="muted small">Turn on notifications. On iPhone, first add Kuch Naya to your Home Screen.</p>
              <div className="row-wrap">
                <button type="button" className="btn btn-sm btn-primary" onClick={enable}>Turn on</button>
                <button type="button" className="btn btn-sm btn-ghost" onClick={() => setState((s) => ({ settings: { ...s.settings, push: false } }))}>Later</button>
              </div>
            </div>
          </div>
        )}
        <div className="chips" role="tablist">
          {['All', 'New episodes', 'Payments'].map((t) => (
            <button key={t} type="button" role="tab" aria-selected={tab === t} className={'chip' + (tab === t ? ' on' : '')} onClick={() => setTab(t)}>{t}</button>
          ))}
        </div>
        {loading && !data && <Loading />}
        {data && list.length === 0 && <p className="empty">Nothing here yet.</p>}
        <ul className="notif-list">
          {list.map((n) => (
            <li key={n.id}>
              <Link to={n.link} className={'notif' + (read[n.id] ? '' : ' unread')} onClick={() => markOne(n.id)}>
                <span className={'notif-icon k-' + n.kind}><Icon name={KIND[n.kind].icon} size={18} /></span>
                <span className="notif-text"><b>{n.title}</b><span>{n.body}</span><small>{ago(n.at)}</small></span>
                {!read[n.id] && <span className="unread-dot" aria-label="New" />}
              </Link>
            </li>
          ))}
        </ul>
      </div>
    </AppShell>
  );
}
